import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { Button } from '../ui/button';
import { X, Mail, Save, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

interface SendGridSettingsProps {
  customerId: string;
  currentApiKey?: string | null;
  currentFromEmail?: string | null;
  currentFromName?: string | null;
  onClose: () => void;
  onUpdate: () => void;
}

export default function SendGridSettings({
  customerId,
  currentApiKey,
  currentFromEmail,
  currentFromName,
  onClose,
  onUpdate,
}: SendGridSettingsProps) {
  const [apiKey, setApiKey] = useState(currentApiKey || '');
  const [fromEmail, setFromEmail] = useState(currentFromEmail || '');
  const [fromName, setFromName] = useState(currentFromName || '');
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        sendgridApiKey: apiKey.trim() || null,
        sendgridFromEmail: fromEmail.trim() || null,
        sendgridFromName: fromName.trim() || null,
        updatedAt: new Date(),
      });

      setShowSuccess(true);
      setTimeout(() => {
        onUpdate();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error updating SendGrid settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">SendGrid Email Configuration</h2>
                <p className="text-sm text-muted-foreground">Configure your own SendGrid account for email notifications</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSave} className="p-6 space-y-6">
          {showSuccess && (
            <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm font-medium">SendGrid settings saved successfully!</p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-400">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-400 mb-2">
              Why configure SendGrid?
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              Configure your own SendGrid account to send email notifications from your domain. This allows you to:
            </p>
            <ul className="text-sm text-blue-800 dark:text-blue-300 list-disc list-inside space-y-1">
              <li>Send emails from your own domain (e.g., notifications@yourdomain.com)</li>
              <li>Have full control over email delivery and analytics</li>
              <li>Avoid shared sender reputation issues</li>
            </ul>
          </div>

          {/* SendGrid API Key */}
          <div>
            <label htmlFor="apiKey" className="block text-sm font-medium text-foreground mb-2">
              SendGrid API Key *
            </label>
            <input
              type="password"
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="SG.xxxxxxxxxxxx"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Get your API key from{' '}
              <a
                href="https://app.sendgrid.com/settings/api_keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                SendGrid Dashboard → Settings → API Keys
              </a>
            </p>
          </div>

          {/* From Email */}
          <div>
            <label htmlFor="fromEmail" className="block text-sm font-medium text-foreground mb-2">
              From Email Address *
            </label>
            <input
              type="email"
              id="fromEmail"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="notifications@yourdomain.com"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              This email must be verified in SendGrid. Visit{' '}
              <a
                href="https://app.sendgrid.com/settings/sender_auth"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Sender Authentication
              </a>{' '}
              to verify.
            </p>
          </div>

          {/* From Name */}
          <div>
            <label htmlFor="fromName" className="block text-sm font-medium text-foreground mb-2">
              From Name
            </label>
            <input
              type="text"
              id="fromName"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Your Company Name"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              The display name that appears in recipient inboxes
            </p>
          </div>

          {/* Setup Instructions */}
          <details className="border border-border rounded-lg">
            <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-foreground hover:bg-muted">
              📖 SendGrid Setup Instructions
            </summary>
            <div className="px-4 pb-4 space-y-3 text-sm text-muted-foreground">
              <div>
                <p className="font-medium text-foreground mb-1">1. Create SendGrid Account</p>
                <p>Sign up for a free account at <a href="https://signup.sendgrid.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">sendgrid.com/signup</a></p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">2. Generate API Key</p>
                <p>Go to Settings → API Keys → Create API Key with "Mail Send" permissions</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">3. Verify Sender</p>
                <p>Go to Settings → Sender Authentication and verify a single sender or your domain</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">4. Enter Details Above</p>
                <p>Paste your API key and verified sender email in the form above</p>
              </div>
            </div>
          </details>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !apiKey.trim() || !fromEmail.trim()}
              className="bg-primary text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
